import { computed, toValue, Transition, TransitionGroup, type MaybeRefOrGetter } from 'vue';
import type { TransitionProps } from '../../types';

interface Classes {
  active: string;
  hidden?: string;
  visible?: string;
}

export function useTransition(
  props: MaybeRefOrGetter<TransitionProps>,
  classes: MaybeRefOrGetter<Classes>,
) {
  const classProps = computed(() => {
    const { hidden, visible } = toValue(classes);
    let { active } = toValue(classes);
    active += ' duration-400';

    return {
      appearActiveClass: active,
      appearFromClass: hidden,
      appearToClass: visible,
      enterActiveClass: active,
      enterFromClass: hidden,
      enterToClass: visible,
      leaveActiveClass: active,
      leaveFromClass: visible,
      leaveToClass: hidden,
    };
  });

  const componentIs = computed(() => (toValue(props).group ? TransitionGroup : Transition));

  const componentProps = computed(() => {
    const { group, ..._props } = toValue(props);

    return {
      appear: _props.appear,
      ...(!group && { mode: _props.mode }),
      onAfterAppear: _props.onAfterAppear,
      onAfterEnter: _props.onAfterEnter,
      onAfterLeave: _props.onAfterLeave,
      onAppear: _props.onAppear,
      onAppearCancelled: _props.onAppearCancelled,
      onBeforeAppear: _props.onBeforeAppear,
      onBeforeEnter: _props.onBeforeEnter,
      onBeforeLeave: _props.onBeforeLeave,
      onEnter: _props.onEnter,
      onEnterCancelled: _props.onEnterCancelled,
      onLeave: _props.onLeave,
      onLeaveCancelled: _props.onLeaveCancelled,
      ...(group && { tag: _props.tag }),
    };
  });

  return {
    classProps,
    componentIs,
    componentProps,
  };
}
